import {
    AccessTime,
    CheckCircleOutline,
    Warning
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Dialog,
    IconButton,
    InputAdornment,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React from "react";
import { useForm } from "react-hook-form";
import CustomTextField from "../../common/CustomTextField";
import { useModalStyles } from "../../common/useModalStyles";
import { FieldLabel } from "../../../utils/Utility";

export type lateFeeConfirmationData = {
  lateFee: string;
};

interface LateFeePopupProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: lateFeeConfirmationData) => void;
  lateFeeAmountLabel?: string;
  lateFeeReceivedAmount?: number;
  currency?: string;
  dueDate?: string;
  daysOverdue?: number;
  zIndex?: number;
}

const LateFeePopup: React.FC<LateFeePopupProps> = ({
  open,
  onClose,
  onConfirm,
  lateFeeAmountLabel = "Late Fee Amount",
  currency = "$",
  lateFeeReceivedAmount,
  dueDate,
  daysOverdue,
  zIndex,
}) => {
  const { control, handleSubmit, reset, watch, formState: { errors } } =
    useForm<lateFeeConfirmationData>({
      defaultValues: {
        lateFee: lateFeeReceivedAmount? lateFeeReceivedAmount.toString() : "",
      },
    });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    inputFieldSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const enteredAmount = watch("lateFee");
  const numericEntered = parseFloat(enteredAmount) || 0;
  const lateFee = lateFeeReceivedAmount ?? 0;
  // const isPartialPayment =
  //   numericEntered > 0 && numericEntered < lateFee;
  // const isFullPayment = numericEntered >= lateFee;
  const onSubmit = (data: lateFeeConfirmationData) => {
    onConfirm(data);
    onClose();
    reset();
  };
  const handleClose = () => {
    reset();
    onClose();
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };
  return (
    <Dialog
      open={open}
      keepMounted
      onClose={handleClose}
      onClick={(event) => event.stopPropagation()}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      sx={zIndex !== undefined ? { zIndex } : undefined}
      PaperProps={{ sx: paperSx(isMobile) }}
      aria-describedby="late-fee-dialog-description"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={headerSx}>
          <Box sx={iconBoxSx("#f59e0b")}>
            <Warning sx={{ fontSize: "1.25rem", color: "#f59e0b" }} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              Add Late Fee
            </Typography>
            <Typography id="late-fee-dialog-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              Add the applicable late fee
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>
        <Box sx={bodySx}>
          <Box
            sx={{
              textAlign: "center",
              p: 2,
              borderRadius: "12px",
              backgroundColor: tokens.cardBg,
              border: `1px solid ${tokens.border}`,
              mb: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "1px",
                fontWeight: 600,
                fontSize: "0.7rem",
                color: tokens.textSecondary,
              }}
            >
              {lateFeeAmountLabel}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: tokens.error,
                mt: 0.5,
              }}
            >
              {formatCurrency(numericEntered > 0 ? numericEntered : (lateFeeReceivedAmount ?? 0))}
            </Typography>
            {daysOverdue && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 1.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "20px",
                }}
              >
                <AccessTime
                  sx={{ fontSize: 14, color: tokens.error }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: tokens.error, fontWeight: 600 }}
                >
                  {daysOverdue} days overdue
                </Typography>
              </Box>
            )}
          </Box>
          <Box>
            <FieldLabel label="Late Fee Amount" required tokens={tokens} />
            <Box sx={inputFieldSx(!!errors.lateFee)}>
              <CustomTextField
                control={control}
                controllerName="lateFee"
                label=""
                placeholder="Enter amount"
                rules={{
                  required: "Late Fee amount is required",
                  validate: (value: string) => {
                    const num = parseFloat(value);
                    if (isNaN(num) || num <= 0) {
                      return "Please enter a valid amount";
                    }
                    return true;
                  },
                }}
                isNumberOnly
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: tokens.textSecondary,
                        }}
                      >
                        {currency}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* {numericEntered > 0 && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    background: alpha(colors.success[400], isDark ? 0.15 : 0.1),
                    border: `1px solid ${
                      isFullPayment
                        ? alpha(colors.success[300], 0.3)
                        : alpha(colors.error[300], 0.3)
                    }`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    fontWeight: "bold",
                  }}
                >
                  {isFullPayment ? (
                    <CheckCircleOutline
                      sx={{ fontSize: 18, color: colors.success[300] }}
                    />
                  ) : (
                    <Warning sx={{ fontSize: 18, color: colors.error[300] }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: isFullPayment
                        ? colors.success[300]
                        : colors.error[300],
                    }}
                  >
                    {isFullPayment
                      ? "Full payment"
                      : lateFeeReceivedAmount !== undefined
                        ? `Partial payment (${formatCurrency(
                          lateFeeReceivedAmount - numericEntered
                        )} remaining)`
                        : "Partial payment"}
                  </Typography>
                </Box>
              )} */}
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              component="button"
              type="submit"
              sx={{
                ...primaryBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "none",
                cursor: "pointer",
                color: "#fff",
                order: { xs: 1, sm: 2 },
              }}
            >
              <CheckCircleOutline sx={{ fontSize: "1.1rem" }} />
              Add Late Fee
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleClose}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
                order: { xs: 2, sm: 1 },
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default LateFeePopup;

